import React, { useState, useEffect, useRef } from 'react'
import { List } from 'lucide-react'

interface TocItem {
  id: string
  text: string
  level: number
}

interface TableOfContentsProps {
  contentRef: React.RefObject<HTMLElement>
}

export const TableOfContents: React.FC<TableOfContentsProps> = ({ contentRef }) => {
  const [tocItems, setTocItems] = useState<TocItem[]>([])
  const [activeId, setActiveId] = useState<string>('')
  const [isOpen, setIsOpen] = useState(true)
  const tocNavRef = useRef<HTMLElement>(null)
  const itemRefs = useRef<Map<string, HTMLLIElement>>(new Map())

  // 生成标题ID
  const generateId = (text: string, index: number = 0): string => {
    const baseId = text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
    
    // 如果生成的ID为空，使用索引作为后备
    return baseId || `heading-${index}`
  }

  // 提取标题并生成目录
  useEffect(() => {
    if (!contentRef.current) return

    // 使用MutationObserver来监听DOM变化，确保在内容加载后也能提取标题
    const updateToc = () => {
      const headings = contentRef.current?.querySelectorAll('h1, h2, h3, h4, h5, h6')
      if (!headings || headings.length === 0) return

      const items: TocItem[] = []
      const usedIds = new Set<string>()

      headings.forEach((heading, index) => {
        const level = parseInt(heading.tagName.charAt(1))
        const text = (heading.textContent || '').trim()
        
        // 跳过空文本的标题
        if (!text) return
        
        // 如果标题还没有ID，生成一个
        let id = heading.id
        if (!id) {
          id = generateId(text, index)
          // 确保ID唯一
          let uniqueId = id
          let counter = 1
          while (usedIds.has(uniqueId)) {
            uniqueId = `${id}-${counter}`
            counter++
          }
          id = uniqueId
          heading.id = id
        }
        
        usedIds.add(id)

        items.push({
          id,
          text,
          level,
        })
      })

      setTocItems(items)
    }

    // 立即执行一次
    updateToc()

    // 使用MutationObserver监听DOM变化
    const observer = new MutationObserver(updateToc)
    observer.observe(contentRef.current, {
      childList: true,
      subtree: true,
    })

    // 延迟执行，确保MDX内容已渲染
    const timeoutId = setTimeout(updateToc, 100)

    return () => {
      observer.disconnect()
      clearTimeout(timeoutId)
    }
  }, [contentRef])

  // 滚动TOC列表，确保指定项可见
  const scrollTocItemIntoView = React.useCallback((id: string) => {
    const itemElement = itemRefs.current.get(id)
    if (itemElement && tocNavRef.current) {
      const navRect = tocNavRef.current.getBoundingClientRect()
      const itemRect = itemElement.getBoundingClientRect()
      
      // 如果项不在可见区域内，滚动到可见位置
      if (itemRect.top < navRect.top || itemRect.bottom > navRect.bottom) {
        itemElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        })
      }
    }
  }, [])

  // 滚动到标题的函数
  const scrollToHeading = React.useCallback((id: string) => {
    const element = document.getElementById(id)
    if (element) {
      const offset = 100 // 考虑固定导航栏的高度
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
      const offsetPosition = elementPosition - offset

      // 更新URL hash（不触发页面跳转）
      window.history.replaceState(null, '', `#${id}`)

      // 平滑滚动到目标位置
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })

      // 设置高亮
      setActiveId(id)
      
      // 确保TOC中的该项可见
      setTimeout(() => scrollTocItemIntoView(id), 100)
    }
  }, [scrollTocItemIntoView])

  // 使用IntersectionObserver监听滚动，高亮当前标题
  useEffect(() => {
    if (tocItems.length === 0) return

    const observerOptions = {
      root: null,
      rootMargin: '-100px 0px -66% 0px', // 当标题进入视口顶部100px位置时触发
      threshold: 0,
    }

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      // 找到所有可见的标题
      const visibleHeadings = entries
        .filter(entry => entry.isIntersecting)
        .map(entry => ({
          id: entry.target.id,
          top: entry.boundingClientRect.top,
        }))
        .sort((a, b) => a.top - b.top)

      if (visibleHeadings.length > 0) {
        // 选择最接近顶部的标题
        const topHeading = visibleHeadings[0]
        setActiveId(topHeading.id)
        
        // 自动滚动TOC列表，确保当前项可见
        scrollTocItemIntoView(topHeading.id)
      }
    }

    const observer = new IntersectionObserver(observerCallback, observerOptions)

    // 观察所有标题元素
    tocItems.forEach(item => {
      const element = document.getElementById(item.id)
      if (element) {
        observer.observe(element)
      }
    })

    // 初始检查：找到当前视口中最接近顶部的标题
    const findInitialActive = () => {
      const headings = tocItems.map(item => {
        const element = document.getElementById(item.id)
        if (!element) return null
        const rect = element.getBoundingClientRect()
        return {
          id: item.id,
          top: rect.top,
          bottom: rect.bottom,
        }
      }).filter(Boolean) as Array<{ id: string; top: number; bottom: number }>

      // 找到第一个在视口上方或视口内的标题
      const viewportTop = 100
      const activeHeading = headings.find(h => h.top <= viewportTop && h.bottom > 0) || 
                            headings[headings.length - 1]

      if (activeHeading) {
        setActiveId(activeHeading.id)
        scrollTocItemIntoView(activeHeading.id)
      }
    }

    // 延迟执行，确保DOM已渲染
    setTimeout(findInitialActive, 100)

    return () => {
      observer.disconnect()
    }
  }, [tocItems, scrollTocItemIntoView])

  // 监听URL hash变化，支持直接通过URL跳转
  useEffect(() => {
    if (tocItems.length === 0) return

    const handleHashChange = () => {
      const hash = window.location.hash.slice(1)
      if (hash && tocItems.some(item => item.id === hash)) {
        scrollToHeading(hash)
      }
    }

    // 初始检查hash
    if (window.location.hash) {
      setTimeout(handleHashChange, 500)
    }

    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [tocItems, scrollToHeading])

  // 条件返回必须在所有Hooks之后
  if (tocItems.length === 0) {
    return null
  }

  return (
    <div className="sticky top-24 self-start">
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        {/* Header */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <div className="flex items-center gap-2">
            <List size={16} className="text-gray-600 dark:text-gray-400" />
            <span className="text-sm font-semibold text-gray-900 dark:text-white">目录</span>
          </div>
          <svg
            className={`w-4 h-4 text-gray-600 dark:text-gray-400 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* TOC Items */}
        {isOpen && (
          <nav 
            ref={tocNavRef}
            className="p-4 max-h-[calc(100vh-200px)] overflow-y-auto scroll-smooth"
          >
            <ul className="space-y-1">
              {tocItems.map((item) => {
                const paddingLeft = `${(item.level - 1) * 16}px`
                const isActive = activeId === item.id
                return (
                  <li 
                    key={item.id}
                    ref={(el) => {
                      if (el) {
                        itemRefs.current.set(item.id, el)
                      } else {
                        itemRefs.current.delete(item.id)
                      }
                    }}
                  >
                    <button
                      onClick={() => scrollToHeading(item.id)}
                      className={`w-full text-left px-2 py-1.5 rounded text-sm transition-all duration-200 ${
                        isActive
                          ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                      style={{ paddingLeft }}
                    >
                      <span className={`relative ${isActive ? 'pl-1' : ''}`}>
                        {isActive && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-4 bg-primary-600 dark:bg-primary-400 rounded-r" />
                        )}
                        {item.text}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </nav>
        )}
      </div>
    </div>
  )
}
